import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Images, Megaphone, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  useProductCampaignsAdmin,
  useCreateProductCampaign,
  useUpdateProductCampaign,
  useDeleteProductCampaign,
  useProductListAdmin,
} from '@/lib/productCatalog'
import { productMediaApi } from '@/api/productMedia'
import { userMessage } from '@/api/errors'
import type { ProductCampaign, ProductCampaignInput } from '@/types/ecommerce'
import EditModal, { type EditField } from '@/components/crud/EditModal'
import CampaignMediaModal from '@/components/crud/CampaignMediaModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { InlineToggle } from '@/components/crud/InlineToggle'
import { resolveMediaUrl } from '@/utils/mediaUrl';

/** datetime-local inputs give/expect "YYYY-MM-DDTHH:mm" with no timezone — treated as UTC
 * directly (not the browser's local zone) so there's no local↔UTC conversion to get wrong;
 * the field hint below tells the admin it's UTC. */
const isoToLocalInput = (iso: string) => iso.slice(0, 16)
const localInputToIso = (value: string) => (value.length === 16 ? `${value}:00Z` : value)

/** Matches the backend CampaignSection enum exactly (see MerchandisingDomain.cs) — labels are
 * just the enum value split on capitals for readability. */
const HOMEPAGE_SECTIONS = [
  'Hero', 'ShopByCategory', 'ShopByBrand', 'DiscoverMore', 'MoreWaysToShop', 'TrendingNow',
  'Inspiration', 'PopularOffers', 'TopPicks', 'Featured', 'NewArrivals', 'Bestsellers',
] as const
const HOMEPAGE_SECTION_OPTIONS = HOMEPAGE_SECTIONS.map((s) => ({ value: s, label: s.replace(/([A-Z])/g, ' $1').trim() }))

function statusLabel(c: ProductCampaign): { text: string; className: string } {
  if (!c.active) return { text: 'Paused', className: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' }
  if (c.isRunning) return { text: 'Running', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
  const now = new Date()
  if (new Date(c.endsAt) < now) return { text: 'Expired', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' }
  return { text: 'Scheduled', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' }
}

function ProductCampaignsPage() {
  const { data: campaigns = [], isLoading, isError, error, isFetching, refetch } = useProductCampaignsAdmin()
  const { data: productPage } = useProductListAdmin({ status: 'Published', pageSize: 100 })
  const create = useCreateProductCampaign()
  const update = useUpdateProductCampaign()
  const del = useDeleteProductCampaign()
  const confirmDelete = useDeleteConfirm()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<ProductCampaign | null>(null)
  const [managingMedia, setManagingMedia] = useState<ProductCampaign | null>(null)

  const productOptions = useMemo(
    () => (productPage?.items ?? []).map((p) => ({ value: p.id, label: p.name })),
    [productPage],
  )

  const fields: EditField[] = [
    { key: 'title', label: 'Title', required: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    {
      key: 'bannerImageUrl',
      label: 'Banner image',
      required: true,
      type: 'file',
      accept: 'image/jpeg,image/png,image/gif,image/webp',
      previewKind: 'image',
      uploadFn: (file) => productMediaApi.uploadCampaignImage(file),
      hint: 'JPEG, PNG, GIF or WEBP, up to 8MB.',
    },
    {
      key: 'bannerVideoUrl',
      label: 'Banner video (optional)',
      type: 'file',
      accept: 'video/mp4,video/webm,video/quicktime',
      previewKind: 'video',
      uploadFn: (file) => productMediaApi.uploadCampaignVideo(file),
      hint: 'MP4, WEBM or MOV, up to 100MB.',
    },
    { key: 'discountPercent', label: 'Discount %', type: 'number', placeholder: 'Leave blank for no badge' },
    { key: 'startsAt', label: 'Starts (UTC)', type: 'datetime-local', required: true },
    { key: 'endsAt', label: 'Ends (UTC)', type: 'datetime-local', required: true },
    { key: 'sortOrder', label: 'Sort order', type: 'number', required: true },
    { key: 'active', label: 'Active', type: 'checkbox', placeholder: 'Eligible to show on the storefront' },
    { key: 'productIds', label: 'Products in this offer', type: 'multiselect', options: productOptions },
    {
      key: 'section',
      label: 'Homepage section',
      type: 'select',
      options: [{ value: '', label: '— Not on homepage —' }, ...HOMEPAGE_SECTION_OPTIONS],
      hint: 'Placing this campaign into a section is separate from the "Active" offers carousel above — both can be on at once.',
    },
    { key: 'showOnHomepage', label: 'Show on homepage', type: 'checkbox', placeholder: 'Only takes effect once a section is chosen above' },
    { key: 'sectionOrder', label: 'Section order', type: 'number', placeholder: '0 = first' },
    { key: 'sectionTitle', label: 'Section title override', placeholder: 'Defaults to the section name if left blank' },
    { key: 'sectionSubtitle', label: 'Section subtitle' },
    { key: 'backgroundColor', label: 'Section background color', type: 'color' },
  ]

  const toInput = (values: Record<string, any>): ProductCampaignInput => ({
    title: values.title,
    description: values.description || undefined,
    bannerImageUrl: values.bannerImageUrl,
    bannerVideoUrl: values.bannerVideoUrl || undefined,
    discountPercent: values.discountPercent === '' || values.discountPercent == null ? null : Number(values.discountPercent),
    startsAt: localInputToIso(values.startsAt),
    endsAt: localInputToIso(values.endsAt),
    active: !!values.active,
    sortOrder: Number.isFinite(values.sortOrder) ? values.sortOrder : 0,
    productIds: Array.isArray(values.productIds) ? values.productIds : [],
    section: values.section || null,
    sectionOrder: Number.isFinite(values.sectionOrder) ? values.sectionOrder : 0,
    sectionTitle: values.sectionTitle || null,
    sectionSubtitle: values.sectionSubtitle || null,
    backgroundColor: values.backgroundColor || null,
    showOnHomepage: !!values.showOnHomepage,
  })

  const sorted = [...campaigns].sort((a, b) => a.sortOrder - b.sortOrder)

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Megaphone className="w-6 h-6" /> Campaigns &amp; Offers
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Limited sales and festive offers shown in the storefront&rsquo;s &ldquo;Special offers&rdquo; carousel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading campaigns…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load campaigns: {(error as Error)?.message}.</p>
        ) : sorted.length === 0 ? (
          <p className="p-6 text-gray-500">No campaigns yet — create one to populate the storefront&rsquo;s offers carousel.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Banner</th>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Discount</th>
                <th className="px-4 py-2">Window (UTC)</th>
                <th className="px-4 py-2">Products</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const status = statusLabel(c)
                return (
                  <tr key={c.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-4 py-3">
                      <img src={resolveMediaUrl(c.bannerImageUrl)} alt="" className="h-12 w-20 rounded-md object-cover bg-gray-100 dark:bg-gray-700" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800 dark:text-gray-100">{c.title}</div>
                      <div className="text-xs text-gray-500 max-w-xs truncate">{c.description}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {c.discountPercent != null ? `${c.discountPercent}%` : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {isoToLocalInput(c.startsAt)} → {isoToLocalInput(c.endsAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{c.products.length}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <InlineToggle
                          checked={c.active}
                          onToggle={(next) =>
                            update.mutateAsync({
                              id: c.id,
                              body: {
                                title: c.title,
                                description: c.description,
                                bannerImageUrl: c.bannerImageUrl,
                                bannerVideoUrl: c.bannerVideoUrl,
                                discountPercent: c.discountPercent,
                                startsAt: c.startsAt,
                                endsAt: c.endsAt,
                                sortOrder: c.sortOrder,
                                active: next,
                                productIds: c.productIds,
                                section: c.section,
                                sectionOrder: c.sectionOrder,
                                sectionTitle: c.sectionTitle,
                                sectionSubtitle: c.sectionSubtitle,
                                backgroundColor: c.backgroundColor,
                                showOnHomepage: c.showOnHomepage,
                              },
                            })
                          }
                        />
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>{status.text}</span>
                      </div>
                      {c.showOnHomepage && c.section && (
                        <div className="mt-1 text-xs text-primary-600 dark:text-primary-400">
                          Homepage: {c.section.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setManagingMedia(c)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Manage media gallery">
                          <Images className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditing(c)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit campaign">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            confirmDelete(c.title, async () => {
                              await del.mutateAsync(c.id)
                            })
                          }
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                          title="Delete campaign"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Campaign"
        fields={fields}
        initialValues={{ sortOrder: campaigns.length, active: true, productIds: [], section: '', sectionOrder: 0, showOnHomepage: false }}
        submitLabel="Create"
        onSubmit={async (values) => {
          try {
            await create.mutateAsync(toInput(values))
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Campaign"
        description={editing?.title}
        fields={fields}
        initialValues={
          editing
            ? {
                title: editing.title,
                description: editing.description,
                bannerImageUrl: editing.bannerImageUrl,
                bannerVideoUrl: editing.bannerVideoUrl ?? '',
                discountPercent: editing.discountPercent ?? '',
                startsAt: isoToLocalInput(editing.startsAt),
                endsAt: isoToLocalInput(editing.endsAt),
                sortOrder: editing.sortOrder,
                active: editing.active,
                productIds: editing.productIds,
                section: editing.section ?? '',
                sectionOrder: editing.sectionOrder,
                sectionTitle: editing.sectionTitle ?? '',
                sectionSubtitle: editing.sectionSubtitle ?? '',
                backgroundColor: editing.backgroundColor ?? '',
                showOnHomepage: editing.showOnHomepage,
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          try {
            await update.mutateAsync({ id: editing.id, body: toInput(values) })
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />

      <CampaignMediaModal
        isOpen={!!managingMedia}
        onClose={() => setManagingMedia(null)}
        campaignId={managingMedia?.id ?? null}
        campaignTitle={managingMedia?.title}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/product-campaigns/')({
  component: ProductCampaignsPage,
})
